import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { pushEvent, ANALYTICS_EVENTS } from "../../utils/analytics";
import { decodeBase64UrlJson, getOAuthError, getOAuthPayload, getOAuthState } from "../../utils/oauthPayload";
import { createSavedBrokerCredentialFromOAuth } from "../../utils/savedSecrets";
import useUser from "../../hooks/useUser";
import "./AlpacaSuccess.css";

function readStoredDeployParams() {
  const saved = sessionStorage.getItem("alpaca_oauth_deploy_params");
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch (_e) {
    return null;
  }
}

function readStateDeployParams(state) {
  const decoded = decodeBase64UrlJson(state);
  if (!decoded || typeof decoded !== "object") return null;

  const params = decoded.deployParams || decoded.returnTo?.deployParams;
  if (!params || typeof params !== "object") return null;
  return params;
}

function buildDeployUrl(deployParams) {
  if (!deployParams) return "/deploy";

  const params = new URLSearchParams();
  if (deployParams.linkedStrategyId) {
    params.set("linkedStrategyId", deployParams.linkedStrategyId);
  } else if (deployParams.strategyId) {
    params.set("strategyId", deployParams.strategyId);
  }

  if (deployParams.aiStrategyId) {
    params.set("aiStrategyId", deployParams.aiStrategyId);
  }
  if (deployParams.aiStrategyRevisionId) {
    params.set("aiStrategyRevisionId", deployParams.aiStrategyRevisionId);
  }
  const query = params.toString();
  return query ? `/deploy?${query}` : "/deploy";
}

function readBrokerConnectionParams() {
  const saved = sessionStorage.getItem("alpaca_oauth_broker_connection_params");
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch (_e) {
    return null;
  }
}

function readStateBrokerConnectionParams(state) {
  const decoded = decodeBase64UrlJson(state);
  if (!decoded || typeof decoded !== "object") return null;
  if (decoded.source !== "broker_connection" || decoded.broker !== "alpaca") return null;

  return {
    broker: "alpaca",
    displayName: typeof decoded.displayName === "string" ? decoded.displayName : "",
    paperOrLive: decoded.paperOrLive === "live" ? "live" : "paper",
    returnTo: typeof decoded.returnTo === "string" && decoded.returnTo.startsWith("/")
      ? decoded.returnTo
      : "/account-settings/broker-connections",
  };
}

function getAlpacaAccountId(payload) {
  return String(
    payload?.account_id ||
    payload?.accountId ||
    payload?.account_number ||
    payload?.accountNumber ||
    ""
  );
}

export default function AlpacaSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getApiToken } = useUser();

  const payload = getOAuthPayload(location);
  const state = getOAuthState(location);
  const error = getOAuthError(location);

  const [encryptionError, setEncryptionError] = useState(null);

  const parsedPayload = useMemo(() => {
    if (!payload) return null;
    return decodeBase64UrlJson(payload);
  }, [payload]);

  const deployParams = useMemo(() => {
    return readStoredDeployParams() || readStateDeployParams(state);
  }, [state]);

  const deployUrl = useMemo(() => buildDeployUrl(deployParams), [deployParams]);
  const brokerConnectionParams = useMemo(() => {
    return readBrokerConnectionParams() || readStateBrokerConnectionParams(state);
  }, [state]);

  useEffect(() => {
    if (error === "expired") {
      pushEvent({
        event: ANALYTICS_EVENTS.OAUTH_ALPACA_EXPIRED,
        source: "alpaca_success_page",
      });
    } else if (parsedPayload) {
      pushEvent({
        event: ANALYTICS_EVENTS.OAUTH_ALPACA_SUCCESS,
        source: "alpaca_success_page",
      });
    } else {
      pushEvent({
        event: ANALYTICS_EVENTS.OAUTH_ALPACA_LOADING,
        source: "alpaca_success_page",
      });
    }
  }, [error, parsedPayload]);

  useEffect(() => {
    if (!parsedPayload || error) return undefined;

    let cancelled = false;
    const saveAndRedirect = async () => {
      try {
        if (brokerConnectionParams || deployParams) {
          const token = await getApiToken();
          const accountId = getAlpacaAccountId(parsedPayload);
          await createSavedBrokerCredentialFromOAuth(token, {
            broker: "alpaca",
            displayName: brokerConnectionParams?.displayName || undefined,
            paperOrLive: brokerConnectionParams?.paperOrLive || "paper",
            externalAccountId: accountId || null,
            externalAccountLabel: accountId || null,
            providerMetadata: {
              accountNumber: accountId || "",
            },
            credentialValues: {
              ALPACA_OAUTH_TOKEN: String(parsedPayload.access_token || parsedPayload.token || payload),
            },
          });

          sessionStorage.removeItem("alpaca_oauth_broker_connection_params");
          sessionStorage.removeItem("alpaca_oauth_deploy_params");
          if (!cancelled) {
            window.setTimeout(() => {
              navigate(brokerConnectionParams?.returnTo || deployUrl, { replace: true });
            }, 500);
          }
          return;
        }

        localStorage.removeItem("alpaca_oauth_tokens");
        if (!cancelled) {
          setEncryptionError("Start broker linking from BotSpot so credentials can be saved securely.");
        }
      } catch (e) {
        console.error("[AlpacaSuccess] Error saving broker connection:", e);
        if (!cancelled) {
          setEncryptionError("Failed to save Alpaca connection. Please try connecting again.");
        }
      }
    };

    saveAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [brokerConnectionParams, deployParams, deployUrl, error, getApiToken, navigate, parsedPayload, payload]);

  if (error === "expired") {
    return (
      <div className="alpaca-oauth-container">
        <h2 className="alpaca-oauth-headline">Session Expired</h2>
        <p>Your Alpaca authorization session has expired. Please try again.</p>
        <Link to={deployUrl} className="alpaca-oauth-try-again-btn">
          Back to Deploy
        </Link>
      </div>
    );
  }

  if (encryptionError) {
    return (
      <div className="alpaca-oauth-container">
        <h2 className="alpaca-oauth-headline">Alpaca Connected</h2>
        <p>{encryptionError}</p>
        <Link to={deployUrl} className="alpaca-oauth-try-again-btn">
          Back to Deploy
        </Link>
      </div>
    );
  }

  return (
    <div className="alpaca-oauth-container">
      <div className="alpaca-oauth-checkmark">✓</div>
      <h2 className="alpaca-oauth-headline">
        {parsedPayload ? "Alpaca Connected" : "Connecting to Alpaca"}
      </h2>
      <div className="alpaca-oauth-auto-redirect">
        <p>Redirecting you back to {brokerConnectionParams ? "broker connections" : "deploy your strategy"}...</p>
        <div className="alpaca-oauth-spinner" />
      </div>
    </div>
  );
}
